/**
 * Rapier3D 물리 월드 래퍼 (research §1/§2).
 * 고정 스텝은 Clock이 결정하고 여기서는 step() 1회 = 1 고정 스텝. 충돌 시작 이벤트를
 * EventQueue로 드레인해 콜라이더 메타와 함께 반환한다(스텝 순회 중 바디 변형 금지 →
 * 호출자가 스텝 이후 처리). 스텝 직전 동적 바디 속도를 스냅샷해 각 충돌 쌍에
 * pre-impact 속도(preVelA/preVelB)로 붙인다 — 스텝이 충돌을 해소한 뒤의 linvel()은
 * 반동/감쇠로 크게 줄어 파괴 판정에 쓸 수 없기 때문.
 */
import RAPIER from '@dimforge/rapier3d-compat';
import type { Vec3 } from '../core/types';

export type ColliderKind = 'static' | 'object' | 'debris' | 'ceiling';

export interface ColliderMeta {
  kind: ColliderKind;
  instanceId?: number;
  defId?: string;
  debrisId?: number;
}

export interface ContactPair {
  a: ColliderMeta;
  b: ColliderMeta;
  bodyA: RAPIER.RigidBody;
  bodyB: RAPIER.RigidBody;
  /** 스텝 직전(pre-impact) bodyA 선속도. 정적/키네마틱이면 영벡터. */
  preVelA: Vec3;
  /** 스텝 직전(pre-impact) bodyB 선속도. 정적/키네마틱이면 영벡터. */
  preVelB: Vec3;
}

/** 스냅샷에 없는 바디(정적/키네마틱)용 공용 영벡터 — 읽기 전용으로만 사용할 것. */
const ZERO_VEL: Vec3 = { x: 0, y: 0, z: 0 };

export class PhysicsWorld {
  world!: RAPIER.World;
  RAPIER = RAPIER;
  private eventQueue!: RAPIER.EventQueue;
  private meta = new Map<number, ColliderMeta>(); // colliderHandle → meta
  private dynamicBodies = new Set<RAPIER.RigidBody>();
  private lastStepMs = 0;
  /**
   * bodyHandle → 스텝 직전 선속도 스냅샷. 슬롯 객체를 재사용(mutate)해 매 스텝 할당을 없앤다.
   * 죽은 바디 슬롯은 removeBody에서 정리. ContactPair가 이 슬롯을 참조하지만 스텝 직후
   * process()에서 동기적으로 소비되므로(다음 step의 mutate 이전) 앨리어싱 문제는 없다.
   */
  private preVel = new Map<number, Vec3>();

  static async create(): Promise<PhysicsWorld> {
    await RAPIER.init();
    const pw = new PhysicsWorld();
    pw.world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    pw.eventQueue = new RAPIER.EventQueue(true);
    return pw;
  }

  get dynamicCount(): number {
    return this.dynamicBodies.size;
  }

  get stepMs(): number {
    return this.lastStepMs;
  }

  /** 깨어 있는 동적 바디 존재 여부 — 유휴 렌더 게이팅의 활동 신호(main.ts 배선). */
  anyDynamicAwake(): boolean {
    for (const body of this.dynamicBodies) if (!body.isSleeping()) return true;
    return false;
  }

  /** 정적 큐보이드 콜라이더 생성. 반환된 body로 제거 가능(책상 파괴 등). */
  addStaticCuboid(pos: Vec3, half: Vec3, kind: ColliderKind = 'static'): RAPIER.RigidBody {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(pos.x, pos.y, pos.z),
    );
    // COLLISION_EVENTS: Rapier는 쌍 중 한쪽만 켜져 있어도 이벤트를 발행한다.
    // 파편 콜라이더는 events=false(파편끼리 n² 스팸 방지)이므로, 정적 쪽에 켜야
    // debris↔static 클래터(debris:impact → AudioBus.playDebris) 경로가 살아난다.
    const col = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(half.x, half.y, half.z)
        .setRestitution(0.3)
        .setFriction(0.8)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      body,
    );
    this.meta.set(col.handle, { kind });
    return body;
  }

  /**
   * 동적 바디 생성. 콜라이더 메타는 별도로 attachCollider/attachCuboid에서 등록한다.
   * @param ccd 고속 터널링 방지 CCD. 파괴 대상 오브젝트(빠른 던지기)는 기본 true.
   *   파편(DebrisPool)만 false — 수십~120개가 버스트로 동시에 CCD shape-cast를 타면 고정
   *   스텝 CPU가 부풀고, 터널링은 두꺼운 책상 콜라이더·앞턱(main.ts)으로 이미 완화돼 있다.
   *   책상 슬랩(DeskBreak)은 1~2개뿐이고 얇은 판이 고속 회전하며 물건을 쳐내므로 true 유지
   *   — false로 바꾸면 flip 피날레의 전량 파괴 신뢰성이 떨어진다(DeskBreak 주석 참조).
   */
  addDynamicBody(pos: Vec3, ccd = true): RAPIER.RigidBody {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(pos.x, pos.y, pos.z)
        .setLinearDamping(0.05)
        .setAngularDamping(0.15)
        .setCcdEnabled(ccd),
    );
    this.dynamicBodies.add(body);
    return body;
  }

  attachCollider(
    body: RAPIER.RigidBody,
    desc: RAPIER.ColliderDesc,
    mass: number,
    meta: ColliderMeta,
    events = true,
  ): RAPIER.Collider {
    let d = desc.setMass(mass).setRestitution(0.35).setFriction(0.7);
    if (events) d = d.setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    const col = this.world.createCollider(d, body);
    this.meta.set(col.handle, meta);
    return col;
  }

  attachCuboid(
    body: RAPIER.RigidBody,
    half: Vec3,
    mass: number,
    meta: ColliderMeta,
    events = true,
  ): RAPIER.Collider {
    return this.attachCollider(body, RAPIER.ColliderDesc.cuboid(half.x, half.y, half.z), mass, meta, events);
  }

  removeBody(body: RAPIER.RigidBody): void {
    // 부착 콜라이더 메타 정리.
    for (let i = 0; i < body.numColliders(); i++) {
      const c = body.collider(i);
      this.meta.delete(c.handle);
    }
    this.dynamicBodies.delete(body);
    this.preVel.delete(body.handle); // 풀 슬롯 회수(핸들 재사용 시 오염 방지)
    this.world.removeRigidBody(body);
  }

  /** 고정 스텝 1회. 시작된 충돌 쌍을 pre-impact 속도·메타와 함께 반환. */
  step(): ContactPair[] {
    const t0 = performance.now();
    // 스텝이 충돌을 해소(반동/감쇠)하기 전의 속도를 스냅샷 — 파괴 판정의 기준 속도.
    // 슬롯 재사용(mutate)으로 매 스텝 할당 0.
    for (const body of this.dynamicBodies) {
      const v = body.linvel();
      const slot = this.preVel.get(body.handle);
      if (slot) {
        slot.x = v.x;
        slot.y = v.y;
        slot.z = v.z;
      } else {
        this.preVel.set(body.handle, { x: v.x, y: v.y, z: v.z });
      }
    }
    this.world.step(this.eventQueue);
    const pairs: ContactPair[] = [];
    this.eventQueue.drainCollisionEvents((h1, h2, started) => {
      if (!started) return;
      const c1 = this.world.getCollider(h1);
      const c2 = this.world.getCollider(h2);
      if (!c1 || !c2) return;
      const m1 = this.meta.get(h1);
      const m2 = this.meta.get(h2);
      const b1 = c1.parent();
      const b2 = c2.parent();
      if (!m1 || !m2 || !b1 || !b2) return;
      pairs.push({
        a: m1,
        b: m2,
        bodyA: b1,
        bodyB: b2,
        preVelA: this.preVel.get(b1.handle) ?? ZERO_VEL,
        preVelB: this.preVel.get(b2.handle) ?? ZERO_VEL,
      });
    });
    this.lastStepMs = performance.now() - t0;
    return pairs;
  }
}
